import { FC, useState, useEffect } from 'react';
import { Title, Text, Paper, Stack, Group } from '@mantine/core';
import { availableDatasets } from './data';
import { Gender } from './types/data-set.types';
import { ChartType } from './types/chart-option.types';
import {
  heightAgeRangeOptions,
  weightAgeRangeOptions,
  bmiAgeRangeOptions,
  headCircumferenceAgeRangeOptions,
} from './options/age-range-options';
import { weightForLengthOptions } from './options/weight-for-length-options';
import { useGrowthChartData } from './hooks/useGrowthChartData';
import { GrowthChartControls } from './components/GrowthChartControls';
import { GrowthChart } from './components/GrowthChart';
import { GrowthChartHeader } from './components/GrowthChartHeader';
import { SummaryTabBlockChildProps } from '../summary-tab/SummaryTab';
import {
  ageObjectToUnit,
  mapObservationsToGrowthCharts,
  pairWeightAndHeight,
} from './utils/growth-chart-mapping';
import { useTranslation } from 'react-i18next';
import usePatientMeasurments from './hooks/usePatientMeasurments';
import { usePatient } from '@/hooks/usePatient';

const measurementTypeToGroupKey = {
  height: 'height',
  weight: 'weight',
  bmi: 'bmi',
  head_circumference: 'head_circumference',
  length: 'height',
} as const;

// -----------------------------------------------------------------------------
interface GrowthChartProps extends SummaryTabBlockChildProps {
  showTitle?: boolean;
}

// -----------------------------------------------------------------------------
export const GrowthCharts: FC<GrowthChartProps> = ({
  showTitle = true,
  minimizedView = false,
}) => {
  const [selectedChartType, setSelectedChartType] =
    useState<ChartType>('height');

  const defaultSelectedOptions: Record<ChartType, string> = {
    height: heightAgeRangeOptions[1].id,
    weight: weightAgeRangeOptions[1].id,
    bmi: bmiAgeRangeOptions[0].id,
    head_circumference: headCircumferenceAgeRangeOptions[0].id,
    weight_for_length: weightForLengthOptions[0].id,
  };

  const [selectedOptionsByChart, setSelectedOptionsByChart] = useState<
    Record<ChartType, string>
  >(defaultSelectedOptions);

  const [showPatientData, setShowPatientData] = useState<boolean>(true);
  const [showAdjustedData, setShowAdjustedData] = useState<boolean>(false);
  const { t } = useTranslation('patient-pages');

  const patient = usePatient();
  const getDefaultGender = (patientGender?: string): Gender => {
    if (patientGender === 'male') {
      return 'male';
    }
    if (patientGender === 'female') {
      return 'female';
    }
    return 'male';
  };
  const { data: observations } = usePatientMeasurments(patient?.id);
  const [selectedGender, setSelectedGender] = useState<Gender>(
    getDefaultGender(patient?.gender)
  );

  useEffect(() => {
    if (patient?.gender) {
      const newGender = getDefaultGender(patient.gender);
      if (newGender !== selectedGender) {
        setSelectedGender(newGender);
      }
    }
  }, [patient?.gender, selectedGender]);

  // ---------------------------------------------------------------------------
  const activeOptions =
    selectedChartType === 'height'
      ? heightAgeRangeOptions
      : selectedChartType === 'weight'
        ? weightAgeRangeOptions
        : selectedChartType === 'bmi'
          ? bmiAgeRangeOptions
          : selectedChartType === 'head_circumference'
            ? headCircumferenceAgeRangeOptions
            : weightForLengthOptions;

  // ---------------------------------------------------------------------------
  const selectedOption = selectedOptionsByChart[selectedChartType];

  useEffect(() => {
    if (
      activeOptions.length > 0 &&
      !activeOptions.some((opt) => opt.id === selectedOption)
    ) {
      setSelectedOptionsByChart((prev) => ({
        ...prev,
        [selectedChartType]: activeOptions[0].id,
      }));
    }
  }, [activeOptions, selectedOption, selectedChartType]);

  // ---------------------------------------------------------------------------
  const currentOption =
    activeOptions.find((option) => option.id === selectedOption) ||
    activeOptions[0];
  const datasetId = currentOption.datasetId[selectedGender];
  const selectedDataset = availableDatasets[datasetId];
  const measurementType = currentOption.measurementType;
  const birthdate = patient?.birthDate ?? '';

  const groupedData = mapObservationsToGrowthCharts(observations, birthdate);
  const gestationalAgeAtBirth =
    groupedData.gestationalAge.observations[0]?.value;

  const groupKey =
    measurementTypeToGroupKey[
      measurementType as keyof typeof measurementTypeToGroupKey
    ];
  const intervalType = selectedDataset.intervalType ?? 'month';
  // Determine if this is a weight-for-length chart
  const isWeightForLength = selectedChartType === 'weight_for_length';

  let patientData: any[] = [];

  if (isWeightForLength) {
    // Pair weights and heights for weight-for-length chart
    patientData = pairWeightAndHeight(
      groupedData.weight?.observations ?? [],
      groupedData.height?.observations ?? []
    ).map((item) => ({ ...item, ageObj: undefined })); // No ageObj for weight-for-length
  } else if (groupKey) {
    // Usual logic for other chart types
    patientData = groupedData[groupKey].observations.map((obs) => {
      return {
        ...obs,
        age: ageObjectToUnit(obs.age, intervalType),
        ageObj: obs.age, // Attach the original age object
      };
    });
  }

  const patientDataForHook = {
    measurements: patientData,
    gestationalAgeAtBirth: gestationalAgeAtBirth,
  };

  // ---------------------------------------------------------------------------

  // Use the custom hook for data processing
  const {
    percentileData,
    patientPoints,
    adjustedPatientPoints,
    getChartLabels,
  } = useGrowthChartData({
    selectedDataset,
    currentOption,
    patientData: patientDataForHook,
    showPatientData,
    showAdjustedData,
    isWeightForLength,
  });

  // ---------------------------------------------------------------------------
  const measurementTypeText =
    selectedChartType === 'height'
      ? currentOption.measurementType === 'height'
        ? 'Height'
        : 'Length'
      : selectedChartType === 'weight'
        ? 'Weight'
        : selectedChartType === 'bmi'
          ? 'BMI'
          : selectedChartType === 'head_circumference'
            ? 'Head Circumference'
            : 'Weight-for-length';

  // ---------------------------------------------------------------------------
  // Get chart labels and formatting helpers
  const chartLabels = getChartLabels(
    selectedDataset,
    measurementTypeText,
    isWeightForLength,
    selectedGender,
    currentOption
  );

  // ---------------------------------------------------------------------------
  if (minimizedView) {
    return (
      <GrowthChart
        selectedDataset={selectedDataset}
        percentileData={percentileData}
        patientPoints={patientPoints}
        adjustedPatientPoints={adjustedPatientPoints}
        showPatientData={showPatientData}
        showAdjustedData={showAdjustedData && !isWeightForLength}
        isWeightForLength={isWeightForLength}
        chartLabels={chartLabels}
        minimizedView
      />
    );
  }

  // ---------------------------------------------------------------------------
  // Determine the correct chartType key for translation
  let chartTypeKey:
    | 'lengthForAge'
    | 'weightForAge'
    | 'bmiForAge'
    | 'headCircumferenceForAge'
    | 'weightForLength' = 'lengthForAge';
  if (selectedChartType === 'height') {
    chartTypeKey =
      currentOption.measurementType === 'height'
        ? 'lengthForAge'
        : 'lengthForAge';
  } else if (selectedChartType === 'weight') {
    chartTypeKey = 'weightForAge';
  } else if (selectedChartType === 'bmi') {
    chartTypeKey = 'bmiForAge';
  } else if (selectedChartType === 'head_circumference') {
    chartTypeKey = 'headCircumferenceForAge';
  } else if (selectedChartType === 'weight_for_length') {
    chartTypeKey = 'weightForLength';
  }
  const safeAgeRange = currentOption.displayName || '0-5 years';

  // ---------------------------------------------------------------------------
  return (
    <Stack gap="md">
      {showTitle && (
        <Title order={2} mb="md">
          WHO Growth Charts
        </Title>
      )}

      <Paper p="md" radius="md">
        <Stack gap="md">
          <Group align="flex-end" justify="space-between">
            <GrowthChartHeader
              chartType={chartTypeKey}
              gender={selectedGender}
              ageRange={safeAgeRange}
            />

            <GrowthChartControls
              selectedChartType={selectedChartType}
              setSelectedChartType={setSelectedChartType}
              selectedGender={selectedGender}
              setSelectedGender={setSelectedGender}
              selectedOption={selectedOption}
              setSelectedOption={(optionId) =>
                setSelectedOptionsByChart((prev) => ({
                  ...prev,
                  [selectedChartType]: optionId,
                }))
              }
              showPatientData={showPatientData}
              setShowPatientData={setShowPatientData}
              showAdjustedData={showAdjustedData}
              setShowAdjustedData={setShowAdjustedData}
              activeOptions={activeOptions}
              isWeightForLength={isWeightForLength}
            />
          </Group>

          <GrowthChart
            selectedDataset={selectedDataset}
            percentileData={percentileData}
            patientPoints={patientPoints}
            adjustedPatientPoints={adjustedPatientPoints}
            showPatientData={showPatientData}
            showAdjustedData={showAdjustedData && !isWeightForLength}
            isWeightForLength={isWeightForLength}
            chartLabels={chartLabels}
          />

          <Text size="xs" c="dimmed" style={{ textAlign: 'center' }}>
            {t('growthCharts.description', {
              growthType: isWeightForLength
                ? t('growthCharts.controls.weightForLength')
                : t('growthCharts.controls.height'),
              dimension: isWeightForLength
                ? t('growthCharts.controls.height')
                : t('growthCharts.controls.ageRange'),
            })}
          </Text>
        </Stack>
      </Paper>
    </Stack>
  );
};
